import * as moment from "moment";
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import {Style, Icon, Text, Fill, Stroke, Circle, RegularShape} from "ol/style";
import LineString from "ol/geom/LineString";
import Plot from "../services/plot";
import {fromLonLat} from "@/olMap/coordUtils";

export const labelFont = 'normal 14px sans-serif';
export const trailLabelFont = labelFont.replace("14px", "12px");
export const labelDirections = [
    {angle: 45, offsetX: 1, offsetY: -1},
    {angle: 135, offsetX: 1, offsetY: 1},
    {angle: 225, offsetX: -1, offsetY: 1},
    {angle: 315, offsetX: -1, offsetY: -1},
    {angle: 0, offsetX: 0, offsetY: -1},
    {angle: 90, offsetX: 1, offsetY: 0},
    {angle: 180, offsetX: 0, offsetY: 1},
    {angle: 270, offsetX: -1, offsetY: 0},
];

const group_icon = [
    createGroupIcon(50, 50, 0, 128, 255),
    createGroupIcon(60, 60, 0, 0, 128),
    createGroupIcon(70, 70, 255, 0, 0),
    createGroupIcon(80, 80, 255, 0, 255),
    createGroupIcon(90, 90, 192, 0, 255),
];
const group_levels = [20, 50, 100, 200, 999999999];

function createGroupIcon(width, height, r, g, b) {
    function loc(d, r) {
        return [+(Math.sin(d * Math.PI / 180) * r + 512).toFixed(2), +(-Math.cos(d * Math.PI / 180) * r + 512).toFixed(2)].join();
    }

    return new Icon({
        src: svg(width, height, `<g transform="scale(${width / 1024}, ${height / 1024})">` +
            `<circle cx="512" cy="512" r="256" fill="rgba(${r}, ${g}, ${b}, 0.8)" />` +
            `<path d="M${loc(320, 311)} A311,311 100 0,1 ${loc(40, 311)} M${loc(80, 311)} A311,311 100 0,1 ${loc(160, 311)} M${loc(200, 311)} A311,311 100 0,1 ${loc(280, 311)}" stroke="rgba(${r}, ${g}, ${b}, 0.6)" stroke-width="60" fill="transparent" />` +
            `<path d="M${loc(320, 396)} A396,396 100 0,1 ${loc(40, 396)} M${loc(80, 396)} A396,396 100 0,1 ${loc(160, 396)} M${loc(200, 396)} A396,396 100 0,1 ${loc(280, 396)}" stroke="rgba(${r}, ${g}, ${b}, 0.4)" stroke-width="60" fill="transparent" />` +
            `<path d="M${loc(320, 481)} A481,481 100 0,1 ${loc(40, 481)} M${loc(80, 481)} A481,481 100 0,1 ${loc(160, 481)} M${loc(200, 481)} A481,481 100 0,1 ${loc(280, 481)}" stroke="rgba(${r}, ${g}, ${b}, 0.2)" stroke-width="60" fill="transparent" />` +
            "</g>"),
        imgSize: [width, height],
    });
}

export function svg(width, height, body) {
    let xml = `<svg xmlns="http://www.w3.org/2000/svg" version="1.1" width="${width}" height="${height}">${body}</svg>`;
    return `data:image/svg+xml,${encodeURIComponent(xml)}`;
}

export function createGroupStyle(g) {
    let level = Math.min(group_icon.length - 1, group_levels.findIndex(x => x > g.size));
    if (level < 0) {
        level = group_icon.length - 1;
    }
    return new Style({
        image: group_icon[level],
        text: new Text({
            text: "" + g.size,
            font: labelFont,
            placement: 'point',
            offsetY: 1,
            fill: new Fill({color: 'black'}),
            stroke: new Stroke({color: 'white', width: 2}),
        }),
    });
}

export let plotTypes = [];

export async function initPlotTypes(addTypes) {
    plotTypes = (await Plot.types() || []).concat(addTypes || []);
}

export function createPlotStyle(plot, direction, labelBkColor, color, type) {
    direction = direction || labelDirections[0];
    type = type || plotTypes.find(x => x.type === plot.type);
    let x = type.width / 2;
    let y = type.height / 2;
    let offsetX = direction.offsetX * (type.nameOffsetX || 0);
    let offsetY = direction.offsetY * (type.nameOffsetY || 0);
    let pathStyle = type.style || `stroke:${color || "black"};fill:${labelBkColor || "white"};`;
    let icon = type.path ? `<path d="${type.path}" style="${pathStyle}"></path>` : "";
    let line = `<line x1="${x}" y1="${y}" x2="${x + offsetX}" y2="${y + offsetY}" stroke="${color || 'red'}" stroke-width="0.5"></line>`;
    let body = `<g>${icon}${line}</g>`;
    return new Style({
        image: new Icon({
            anchor: type.anchor,
            src: svg(type.width, type.height, body),
            imgSize: [type.width, type.height],
        }),
        text: new Text({
            text: plotDisplayName(plot),
            font: labelFont,
            placement: 'point',
            offsetX: 6 * direction.offsetX + offsetX,
            offsetY: offsetY + (direction.offsetY > 0 ? 2 : 0),
            textAlign: direction.offsetX > 0 ? "left" : (direction.offsetX < 0 ? "right" : "center"),
            textBaseline: direction.offsetY > 0 ? "top" : (direction.offsetY < 0 ? "bottom" : "middle"),
            fill: new Fill({color: color || 'black'}),
            backgroundFill: new Fill({color: labelBkColor || 'white'}),
            backgroundStroke: new Stroke({color: color || 'red', width: 0.5}),
            padding: [2.5, 2.5, 0, 5]
        })
    });
}

export function getRectCoordinates(item) {
    return [
        [item.minLon, item.maxLat],
        [item.maxLon, item.maxLat],
        [item.maxLon, item.minLat],
        [item.minLon, item.minLat],
        [item.minLon, item.maxLat],
    ].map(x => fromLonLat(x));
}

export function linePlotDisplayName(item) {
    return [`ID:${item.id}`, item.name, item.berth].filter(x => x).join(" - ");
}

export function rectPlotDisplayName(item) {
    return item.name;
}

export function plotDisplayName(plot) {
    return plot.name;
    // let name = plot.name;
    // if (Array.isArray(plot.aliases) && plot.aliases.length > 0) {
    //     name += "(" + plot.aliases.join() + ")";
    // }
    // return name;
}

export function vesselDisplayName(vessel) {
    return vessel.chnName || vessel.shortName || vessel.name || "——";
}

export function createFeature(point, style, params) {
    let feature = new Feature(Object.assign({geometry: new Point(point)}, params));
    feature.setStyle(style);
    return feature;
}

export const selectedStyle = new Style({
    image: new Icon({
        src: svg(34, 34, '<rect width="34" height="34" fill="transparent"></rect><path fill="transparent" stroke="red" stroke-width="4" stroke-linecap="round" d="M3,10 L3,3 L10,3 M24,3 L31,3 L31,10 M31,24 L31,31 L24,31 M10,31 L3,31 L3,24"></path>'),
        imgSize: [34, 34],
    })
});

export function createVesselStyle(v, isSelect, stroke) {
    stroke = stroke || (isSelect ? "#FF0000" : "#000000");
    let fill = v.recent ? "#00FF00" : (v.in24h ? "#FF7FF4" : "#C0C0C0");

    let size = (v.sog > 0) ? [14, 100] : [20, 20];
    let speedLine = (v.sog > 0) ? `<line x1="7" y1="${41 - Math.min(20, v.sog * 2)}" x2="7" y2="41" stroke="${stroke}" stroke-width="2"></line>` : "";
    let xml = "";
    let head = (v.sog > 0) ? v.cog : 0;
    if (v.senior) {
        xml = (v.sog > 0)
            ? `<polygon points="7,41 1,59 13,59" fill="${fill}" stroke="${stroke}"></polygon>${speedLine}`
            : `<polygon points="10,1 1,10 10,19 19,10" fill="${fill}" stroke="${stroke}"></polygon>`;
        if (v.ship) {
            xml += (v.sog > 0)
                ? `<polygon points="7,47.5 4,56.5 10,56.5" stroke="${stroke}" stroke-width="0.5" fill="#FFFFFF"></polygon>`
                : `<polygon points="10,5.5 5.5,10 10,14.5 14.5,10" stroke="${stroke}" stroke-width="0.5" fill="#FFFFFF"></polygon>`;
        }
    } else if (v.ship) {
        xml = (v.sog > 0)
            ? `<polygon points="7,41 1,47 1,59 13,59 13,47" fill="${fill}" stroke="${stroke}"></polygon>${speedLine}`
            : `<polygon points="19,10 14.5,2.2 5.5,2.2 1,10 5.5,17.8 14.5,17.8" fill="${fill}" stroke="${stroke}"></polygon>`;
        xml += (v.sog > 0)
            ? `<polygon points="7,45.2 4,50 4,56 10,56 10,50" stroke="${stroke}" stroke-width="0.5" fill="#FFFFFF"></polygon>`
            : `<polygon points="14.5,10 12.25,6.1 7.75,6.1 5.5,10 7.75,13.9 12.25,13.9" stroke="${stroke}" stroke-width="0.5" fill="#FFFFFF"></polygon>`;
    } else {
        size = [14, 100];
        xml = `<polygon points="7,41 11,47 11,57 9,59 5,59 3,57 3,47" fill="${fill}" stroke="${stroke}"></polygon>${speedLine}`;
        if (!v.sog) {
            head = (!v.heading || v.sog >= 0.5) ? v.cog : v.heading;
        }
    }
    return new Style({
        image: new Icon({
            src: svg(size[0], size[1], xml),
            imgSize: size,
            rotation: Math.PI * (head || 0) / 180,
        })
    });
}

export function createLabelLineStyle(angle, isSelect, isTrail) {
    let length = isSelect ? 24 : 12;
    let stroke = isSelect ? "red" : (isTrail ? "blue" : "black");
    return new Style({
        image: new Icon({
            src: svg(1, length * 2, `<line x1="0" y1="0" x2="0" y2="${length}" style="stroke:${stroke}"></line>`),
            imgSize: [1, length * 2],
            rotation: Math.PI * angle / 180,
        })
    });
}

export function createLabelStyle(l, isSelect, isTrail, isBerth) {
    let distance = isSelect ? 17 : 9;
    let color = isSelect ? 'red' : (isTrail ? 'blue' : 'black');
    let textColor = isTrail && isBerth ? "magenta" : (isTrail ? 'blue' : color);
    let backgroundColor = "white";
    if (isSelect && isTrail) {
        textColor = "white";
        backgroundColor = isBerth ? "magenta" : "blue";
    }
    return new Style({
        text: new Text({
            text: l.text,
            font: isSelect ? labelFont : trailLabelFont,
            placement: 'point',
            offsetX: l.direction.offsetX * (distance + 5),
            offsetY: l.direction.offsetY * (distance + 2),
            textAlign: l.direction.offsetX > 0 ? "left" : (l.direction.offsetX < 0 ? "right" : "center"),
            textBaseline: l.direction.offsetY > 0 ? "top" : (l.direction.offsetY < 0 ? "bottom" : "middle"),
            fill: new Fill({color: textColor}),
            backgroundFill: new Fill({color: backgroundColor}),
            backgroundStroke: new Stroke({color: color, width: 0.5}),
            padding: [2, 5, 2, 5],
        })
    });
}

const trailLineStyles = {
    sail: new Style({stroke: new Stroke({color: 'blue', width: 3, lineDash: [10, 5]})}),
    berth: new Style({stroke: new Stroke({color: 'blue', width: 3})}),
    timeout: new Style({stroke: new Stroke({color: 'blue', width: 3, lineDash: [1, 8]})}),
};
const trailPointStyles = {
    sail: new Style({
        image: new Circle({
            radius: 3, stroke: new Stroke({color: 'blue', width: 3}), fill: new Fill({color: 'white'}),
        })
    }),
    berth: new Style({
        image: new RegularShape({
            points: 4, radius: 5, stroke: new Stroke({color: 'blue', width: 3}), fill: new Fill({color: 'lightPink'}),
        })
    }),
};
const trailPointHighlightStyles = {
    sail: new Style({
        image: new Circle({
            radius: 5, stroke: new Stroke({color: 'red', width: 3}), fill: new Fill({color: 'white'}),
        })
    }),
    berth: new Style({
        image: new RegularShape({
            points: 4, radius: 7, stroke: new Stroke({color: 'red', width: 3}), fill: new Fill({color: 'lightPink'}),
        })
    }),
};

export function trailPointToolTip(point) {
    let speedDesc = (Number.isFinite(+point.sog) && +point.sog < 102.3) ? `${+(+point.sog.toFixed(2))}节` : "——";
    return `${moment(point.time).format("Y-M-D H:m")}\n航速：${speedDesc}`;
}

export function trailFeatures(trail, resolution, vessel, occupiedRects, map, highlightTime) {
    let features = [];
    occupiedRects = occupiedRects || [];
    if (Array.isArray(trail) && trail.length > 0) {
        let slice = [];
        let lastState = null;
        let addTrailLine = () => {
            if (lastState && slice.length > 1) {
                let feature = new Feature(new LineString(slice.map(x => x.coordinate)));
                feature.setStyle(trailLineStyles[lastState]);
                features.push(feature);
            }
            slice = slice.slice(-1);
        };
        for (let trailPoint of trail) {
            slice.push(trailPoint);
            let state = trailPoint.isTimeoutBegin ? "timeout" : (trailPoint.state || "sail");
            if (state !== lastState) {
                addTrailLine();
                lastState = state;
            }
        }
        addTrailLine();

        let trailNodes = [];
        let pixelCount = 0;
        let lastPixel = vessel.coordinate.map(x => x / resolution);
        for (let trailPoint of trail) {
            let pixel = trailPoint.coordinate.map(x => x / resolution);
            let distanceSquare = getDistanceSquare(pixel, lastPixel);
            if (distanceSquare > 25) {
                lastPixel = pixel;
                pixelCount += Math.sqrt(distanceSquare);
            }
            if (trailPoint.time === highlightTime) {
                if (pixelCount <= 80 && trailNodes.length > 0) {
                    trailNodes.pop();
                }
                trailNodes.push(trailPoint);
                pixelCount = 0;
                lastPixel = pixel;
            } else {
                let lastNode = trailNodes[trailNodes.length - 1];
                if (trailPoint.state === "berth" && pixelCount <= 80
                    && lastNode && lastNode.state !== "berth" && lastNode.time !== highlightTime) {
                    trailNodes.pop();
                    pixelCount = 101;
                }
                if (pixelCount > 100 || trailPoint.state === "berth" && pixelCount > 80) {
                    trailNodes.push(trailPoint);
                    pixelCount = 0;
                    lastPixel = pixel;
                }
            }
        }

        function addTrailPoint(trailPoint, isHighlight) {
            let state = trailPoint.state || "sail";
            let tooltip = trailPointToolTip(trailPoint);
            let style = isHighlight ? trailPointHighlightStyles[state] : trailPointStyles[state];
            features.push(createFeature(trailPoint.coordinate, style, {tooltip: tooltip, trail: trailPoint}));
            let text = moment(trailPoint.time).format("M-D H:m");
            labelItems.push({
                isHighlight: isHighlight,
                isTrail: true,
                id: trailPoint.time,
                coordinate: trailPoint.coordinate,
                text: text,
                size: [measureText(text, trailLabelFont) + 4, 18],
                tooltip: tooltip,
                state: state,
                point: trailPoint,
            });
            if (isHighlight) {
                let pixel = map.getPixelFromCoordinate(trailPoint.coordinate);
                occupiedRects.push([pixel[0] - 5, pixel[1] - 5, pixel[0] + 5, pixel[1] + 5]);
            }
        }

        let labelItems = [];
        for (let trailPoint of trailNodes) {
            if (trailPoint.time !== highlightTime) {
                addTrailPoint(trailPoint, false);
            }
        }
        let highlightPoint = trailNodes.find(x => x.time === highlightTime);
        if (highlightPoint) {
            addTrailPoint(highlightPoint, true);
        }

        features = features.concat(getTrailLabels(labelItems, map, occupiedRects));
    }
    return features;
}

function getDistanceSquare(c1, c2) {
    let deltaX = c1[0] - c2[0];
    let deltaY = c1[1] - c2[1];
    return deltaX * deltaX + deltaY * deltaY;
}

export function measureText(text, font) {
    let canvas = document.createElement('canvas');
    let context = canvas.getContext('2d');
    context.font = font || labelFont;
    return context.measureText(text).width;
}

export function getLabelItem(v, isSelect) {
    if (v.displayName !== "——") {
        return {
            id: v.mmsi,
            coordinate: v.coordinate,
            text: v.name,
            size: [measureText(v.name, isSelect ? labelFont : trailLabelFont) + 14, 24],
            vessel: v,
        };
    } else {
        return null;
    }
}

export function getPlotLabelItem(p, isSelect) {
    if (p.displayName) {
        return {
            id: p.id,
            coordinate: p.coordinate,
            text: p.displayName,
            size: [measureText(p.displayName, isSelect ? labelFont : trailLabelFont) + 14, 24],
            plot: p,
        };
    } else {
        return null;
    }
}

export function getLabelRect(l, direction, map, distance) {
    distance = Number.isFinite(distance) ? distance : (l.isTrail && !l.isHighlight ? 9 : 17);
    let pixel = map.getPixelFromCoordinate(l.coordinate);
    if (pixel) {
        let x1 = pixel[0] + direction.offsetX * distance;
        let x2 = x1 + direction.offsetX * l.size[0];
        if (x1 === x2) {
            x1 -= l.size[0];
            x2 += l.size[0];
        }
        let y1 = pixel[1] + direction.offsetY * distance;
        let y2 = y1 + direction.offsetY * l.size[1];
        if (y1 === y2) {
            y1 -= l.size[1];
            y2 += l.size[1];
        }
        return [Math.min(x1, x2), Math.min(y1, y2), Math.max(x1, x2), Math.max(y1, y2)];
    } else {
        return [0, 0, 0, 0];
    }
}

export function placeLabelItems(candidateLabelItems, map, occupiedRects) {
    candidateLabelItems = candidateLabelItems || [];
    occupiedRects = occupiedRects || [];
    let labelItems = [];

    function tryDirection(l, direction) {
        let rect = getLabelRect(l, direction, map);
        let occupied = false;
        for (let r of occupiedRects) {
            if (r[2] > rect[0] && rect[2] > r[0] && r[3] > rect[1] && rect[3] > r[1]) {
                occupied = true;
                break;
            }
        }
        if (!occupied) {
            l.direction = direction;
            l.rect = rect;
            l.labelDirection = direction;
            occupiedRects.push(rect);
            labelItems.push(l);
            return true;
        } else {
            return false;
        }
    }

    function tryAllDirections(l) {
        for (let direction of labelDirections) {
            if (tryDirection(l, direction)) {
                break;
            }
        }
    }

    function placeLabels() {
        let highlightItem = candidateLabelItems.find(x => x.isHighlight);
        if (highlightItem) {
            tryAllDirections(highlightItem);
        }
        candidateLabelItems.forEach(l => {
            if (!l.isHighlight) {
                tryAllDirections(l);
            }
        });
        candidateLabelItems = [];
    }

    placeLabels();
    return labelItems;
}

function getTrailLabels(candidateLabelItems, map, occupiedRects) {
    let labelItems = placeLabelItems(candidateLabelItems, map, occupiedRects);
    let features = [];
    labelItems.forEach(l => {
        if (l.isTrail) {
            let isBerth = l.state === 'berth';
            features.push(createFeature(l.coordinate, createLabelStyle(l, l.isHighlight, true, isBerth), {tooltip: l.tooltip, trail: l.point}));
            features.push(createFeature(l.coordinate, createLabelLineStyle(l.direction.angle, l.isHighlight, true)));
        }
    });
    return features;
}

export function createThermoFeature(point, weight) {
    return new Feature({geometry: new Point(point), weight});
}

export function createBerthFeature(point, mmsi, berth) {
    let in90Days = new Date(berth.maxTime).getTime() >= moment().startOf("day").subtract(90, "days").valueOf();
    let tooltipLines = [
        `停泊经纬度：${berth.lon.toFixed(6)}, ${berth.lat.toFixed(6)}`,
        `起始时间：${moment(berth.minTime).format("YYYY-MM-DD HH:mm:ss")}`,
        `结束时间：${moment(berth.maxTime).format("YYYY-MM-DD HH:mm:ss")}`,
    ].filter(x => x);
    let fill = new Fill({color: in90Days ? "lightgreen" : "#808080"});
    let stroke = new Stroke({color: 'blue'});
    return createFeature(berth.coordinate, new Style({
        image: new Circle({fill, stroke, radius: 5}), fill, stroke,
    }), {berth: berth, tooltip: tooltipLines.join("\n")});
}
